import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  Wrench,
  Users,
  Key,
  MessageSquare,
  UserCog,
  User,
  Shield,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Building2,
  Code,
  Kanban,
  Clock,
  Workflow,
  HelpCircle,
  LogOut,
  KeyRound,
  Lock,
  Store,
  Server,
  Settings,
  Layers,
  Briefcase,
  FileText,
  Send,
  Globe,
  Database,
  PenTool,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import {
  Button,
  Logo,
  LogoIcon,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetTitle,
  SimpleTooltip,
  UserAvatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui';
import { cn } from '@/lib/utils';

interface NavItem {
  name: string;
  path: string;
  icon: LucideIcon;
  roles?: string[];
}

interface NavGroup {
  id: string;
  name: string;
  icon: LucideIcon;
  items: NavItem[];
  roles?: string[];
}

// Top-level navigation items (no group header)
const topNavItems: NavItem[] = [
  { name: 'Dashboard', path: '/', icon: LayoutDashboard },
];

// Navigation organized into groups - ERP style
const navGroups: NavGroup[] = [
  {
    id: 'operations',
    name: 'Operaciones',
    icon: Briefcase, // Changed from Kanban to avoid duplicate with Trabajos
    items: [
      { name: 'Trabajos', path: '/jobs', icon: Kanban },
      { name: 'Casos', path: '/cases', icon: FileText },
      { name: 'Citas', path: '/bookings', icon: Calendar },
    ],
  },
  {
    id: 'crm',
    name: 'CRM',
    icon: Users,
    items: [
      { name: 'Clientes', path: '/customers', icon: User },
      { name: 'Vehiculos', path: '/vehicles', icon: Wrench },
    ],
  },
  {
    id: 'workshop',
    name: 'Taller',
    icon: Wrench,
    roles: ['admin'],
    items: [
      { name: 'Servicios', path: '/admin/service-types', icon: Layers }, // Changed from Wrench to avoid duplicate
      { name: 'Bahías', path: '/admin/bays', icon: Building2 },
      { name: 'Horarios', path: '/admin/hours', icon: Clock },
    ],
  },
  {
    id: 'commercial',
    name: 'Comercial',
    icon: Store,
    items: [
      { name: 'Marketing', path: '/marketing', icon: Send }, // Changed from Mail to avoid duplicate
      { name: 'Consultas', path: '/inquiries', icon: MessageSquare },
      { name: 'Alquileres', path: '/rentals', icon: Key },
    ],
  },
  {
    id: 'workflow',
    name: 'Procesos',
    icon: Workflow,
    roles: ['admin'],
    items: [
      { name: 'Flujos de Trabajo', path: '/admin/workflows', icon: Workflow },
      { name: 'Reglas de Reserva', path: '/admin/booking-rules', icon: Settings },
      { name: 'Webhooks', path: '/admin/webhooks', icon: Server },
    ],
  },
  {
    id: 'config',
    name: 'Canales',
    icon: Globe,
    roles: ['admin'],
    items: [
      { name: 'Plantillas Email', path: '/admin/templates', icon: FileText },
      { name: 'Plantillas Firma', path: '/admin/signature-templates', icon: PenTool },
      { name: 'Orígenes CORS', path: '/admin/cors-origins', icon: Globe },
      { name: 'Widget Web', path: '/embed-config', icon: Code },
    ],
  },
  {
    id: 'admin',
    name: 'Sistema',
    icon: Server,
    roles: ['admin'],
    items: [
      { name: 'Personal', path: '/admin/staff', icon: UserCog },
      { name: 'Seguridad', path: '/admin/security', icon: Shield },
      { name: 'Auditoría', path: '/admin/audit', icon: ClipboardList },
      { name: 'Utilidades', path: '/admin/utilities', icon: Database },
    ],
  },
];


const COLLAPSED_GROUPS_KEY = 'sidebar-collapsed-groups';

function getStoredCollapsedGroups(): string[] {
  try {
    const stored = localStorage.getItem(COLLAPSED_GROUPS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function setStoredCollapsedGroups(groups: string[]) {
  try {
    localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify(groups));
  } catch {
    // Ignore localStorage errors
  }
}

// Routes that have child pages and should match with startsWith
const ROUTES_WITH_CHILDREN = ['/customers', '/vehicles', '/bookings', '/cases', '/marketing', '/settings', '/help'];

function isNavItemActive(pathname: string, item: NavItem) {
  return pathname === item.path ||
    (ROUTES_WITH_CHILDREN.includes(item.path) && pathname.startsWith(item.path + '/'));
}

function NavItemLink({
  item,
  isCollapsed,
  onClick,
  indent = false,
}: {
  item: NavItem;
  isCollapsed: boolean;
  onClick?: () => void;
  indent?: boolean;
}) {
  const location = useLocation();
  const isActive = isNavItemActive(location.pathname, item);
  const Icon = item.icon;

  const link = (
    <NavLink
      to={item.path}
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        isCollapsed && 'justify-center px-2',
        indent && !isCollapsed && 'ml-2.5'
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!isCollapsed && <span>{item.name}</span>}
    </NavLink>
  );

  if (isCollapsed) {
    return (
      <SimpleTooltip content={item.name} side="right">
        {link}
      </SimpleTooltip>
    );
  }

  return link;
}

function NavGroupSection({
  group,
  isCollapsed,
  isSidebarCollapsed,
  onToggle,
  onItemClick,
  userRole,
}: {
  group: NavGroup;
  isCollapsed: boolean;
  isSidebarCollapsed: boolean;
  onToggle: () => void;
  onItemClick?: () => void;
  userRole?: string;
}) {
  const location = useLocation();
  const Icon = group.icon;

  // Filter items by role
  const visibleItems = group.items.filter(
    (item) => !item.roles || (userRole && item.roles.includes(userRole))
  );

  // Check if any item in group is active
  const hasActiveItem = visibleItems.some((item) => isNavItemActive(location.pathname, item));

  if (visibleItems.length === 0) return null;

  // In collapsed sidebar, show items as a tooltip menu
  if (isSidebarCollapsed) {
    return (
      <div className="space-y-1">
        {visibleItems.map((item) => (
          <NavItemLink
            key={item.path}
            item={item}
            isCollapsed={true}
            onClick={onItemClick}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {/* Group header */}
      <button
        onClick={() => !hasActiveItem && onToggle()}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
          hasActiveItem
            ? 'text-foreground'
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">{group.name}</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform',
            isCollapsed && '-rotate-90'
          )}
        />
      </button>

      {/* Group items */}
      {(!isCollapsed || hasActiveItem) && (
        <div className="space-y-0.5">
          {visibleItems.map((item) => (
            <NavItemLink
              key={item.path}
              item={item}
              isCollapsed={false}
              onClick={onItemClick}
              indent
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarContent({
  isCollapsed,
  onClose,
}: {
  isCollapsed: boolean;
  onClose?: () => void;
}) {
  const { user } = useAuth();
  const { toggleCollapse } = useSidebar();
  const location = useLocation();
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>(() =>
    getStoredCollapsedGroups()
  );
  const initializedRef = useRef(false);

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Filter groups by role
  const visibleGroups = navGroups.filter(
    (group) => !group.roles || (user && group.roles.includes(user.role))
  );

  useEffect(() => {
    const activeGroupIds = visibleGroups
      .filter((group) => group.items.some((item) => isNavItemActive(location.pathname, item)))
      .map((group) => group.id);

    setCollapsedGroups((prev) => {
      if (!initializedRef.current && prev.length === 0) {
        initializedRef.current = true;
        return visibleGroups
          .filter((group) => !activeGroupIds.includes(group.id))
          .map((group) => group.id);
      }
      return prev.filter((id) => !activeGroupIds.includes(id));
    });
  }, [location.pathname, visibleGroups]);

  useEffect(() => {
    setStoredCollapsedGroups(collapsedGroups);
  }, [collapsedGroups]);

  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div
        className={cn(
          'flex h-12 items-center border-b px-3',
          isCollapsed ? 'justify-center' : 'justify-between'
        )}
      >
        {isCollapsed ? (
          <LogoIcon size="sm" />
        ) : (
          <Logo size="sm" />
        )}
        {/* Collapse toggle - only on desktop */}
        {!onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleCollapse}
            className={cn(
              'h-7 w-7 shrink-0',
              isCollapsed && 'absolute -right-3 top-4 z-50 rounded-full border bg-background shadow-md'
            )}
          >
            {isCollapsed ? (
              <ChevronRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronLeft className="h-3.5 w-3.5" />
            )}
            <span className="sr-only">
              {isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            </span>
          </Button>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-3">
        <nav className={cn('space-y-3', isCollapsed ? 'px-1.5' : 'px-2')}>
          {/* Top-level items (Dashboard) */}
          <div className="space-y-0.5">
            {topNavItems.map((item) => (
              <NavItemLink
                key={item.path}
                item={item}
                isCollapsed={isCollapsed}
                onClick={onClose}
              />
            ))}
          </div>

          {/* Grouped navigation */}
          {visibleGroups.map((group) => (
            <NavGroupSection
              key={group.id}
              group={group}
              isCollapsed={collapsedGroups.includes(group.id)}
              isSidebarCollapsed={isCollapsed}
              onToggle={() => toggleGroup(group.id)}
              onItemClick={onClose}
              userRole={user?.role}
            />
          ))}
        </nav>
      </ScrollArea>

      {/* User Menu Footer */}
      <div className="border-t p-2">
        <UserMenuFooter isCollapsed={isCollapsed} />
      </div>
    </div>
  );
}

function UserMenuFooter({ isCollapsed }: { isCollapsed: boolean }) {
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (!user) return null;

  const menuContent = (
    <>
      <DropdownMenuLabel className="font-normal py-1.5">
        <div className="flex flex-col space-y-0.5">
          <p className="text-xs font-medium leading-none">{user.name}</p>
          <p className="text-[10px] leading-none text-muted-foreground">{user.email}</p>
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild className="text-xs py-1.5">
        <Link to="/settings/profile">
          <User className="mr-2 h-3.5 w-3.5" />
          <span>Mi Perfil</span>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild className="text-xs py-1.5">
        <Link to="/settings/password">
          <Lock className="mr-2 h-3.5 w-3.5" />
          <span>Contraseña</span>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild className="text-xs py-1.5">
        <Link to="/settings/passkeys">
          <KeyRound className="mr-2 h-3.5 w-3.5" />
          <span>Passkeys</span>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild className="text-xs py-1.5">
        <Link to="/help">
          <HelpCircle className="mr-2 h-3.5 w-3.5" />
          <span>Ayuda</span>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={handleLogout}
        className="text-destructive focus:text-destructive text-xs py-1.5"
      >
        <LogOut className="mr-2 h-3.5 w-3.5" />
        <span>Cerrar sesión</span>
      </DropdownMenuItem>
    </>
  );

  if (isCollapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="w-full h-8 w-8">
            <UserAvatar name={user.name} size="xs" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="end" className="w-48">
          {menuContent}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-start gap-2 px-2 h-auto py-1.5"
        >
          <UserAvatar name={user.name} size="xs" />
          <div className="flex-1 text-left min-w-0">
            <p className="text-xs font-medium leading-none truncate">{user.name}</p>
            <p className="text-[10px] text-muted-foreground capitalize mt-0.5">{user.role}</p>
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-48">
        {menuContent}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Sidebar() {
  const { isOpen, isCollapsed, close } = useSidebar();

  return (
    <>
      {/* Mobile Sidebar (Sheet) */}
      <Sheet open={isOpen} onOpenChange={(open) => !open && close()}>
        <SheetContent side="left" className="w-56 p-0" aria-describedby={undefined}>
          <SheetTitle className="sr-only">Menú de navegación</SheetTitle>
          <SidebarContent isCollapsed={false} onClose={close} />
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-30 hidden h-screen border-r bg-card transition-all duration-300 lg:flex lg:flex-col',
          isCollapsed ? 'w-14' : 'w-52'
        )}
      >
        <SidebarContent isCollapsed={isCollapsed} />
      </aside>
    </>
  );
}
